package com.fireavert.react.pages.administration.breadcrumbs.super_admin_tools

import com.fireavert.administration.super_admin_tools_page.client.presentation.WirelessIdSectionController
import com.fireavert.administration.super_admin_tools_page.client.presentation.WirelessIdSectionViewModel
import com.fireavert.administration.super_admin_tools_page.data_models.data_model.SmokeAlarmDurationRecordModel
import com.fireavert.administration.super_admin_tools_page.data_models.models.PropertyNotificationStatsModel
import com.fireavert.administration.super_admin_tools_page.data_models.models.PropertyOfflineStatisticsModel
import com.fireavert.administration.users_page.main_user_page.data_models.data_model.UserDTO
import com.fireavert.common.TableColumn
import com.fireavert.common.toOneDecimal
import com.fireavert.events.shared.data_models.models.ReportFrequency
import com.fireavert.menu.menu_navigation.client.RoutePaths
import com.fireavert.properties.shared.data_models.Property
import com.fireavert.react.pages.administration.styles.SuperAdminToolsStyles
import com.fireavert.react.shared.components.forms.slider.ValueLabelDisplay
import com.fireavert.react.shared.components.tables.SortBar
import com.fireavert.react.shared.components.tables.mapToControlColumns
import com.fireavert.react.shared.components.tables.mapToStateColumns
import com.fireavert.react.shared.styles.TableStyles
import com.fireavert.utilities.getKoinInstance
import com.fireavert.utilities.saveAs
import emotion.css.ClassName
import emotion.css.cx
import emotion.react.css
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import mui.material.*
import mui.material.Size
import mui.material.styles.TypographyVariant
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import react.*
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.section
import react.dom.onChange
import react.router.dom.Link
import web.cssom.*
import web.html.HTMLInputElement

// WirelessDeviceAdminPage FC remains the same as in your original code

val SuperAdminToolsSection = FC {
    val controller = getKoinInstance<WirelessIdSectionController>()
    val viewModel = getKoinInstance<WirelessIdSectionViewModel>()

    // --- State Variables ---
    var wirelessDeviceId: String by useState("")
    var deviceLocationLink: String by useState("")
    var unitName: String by useState("")
    var propertyName: String by useState("")
    val (searchValue, setSearchValue) = useState("") // For Device ID Lookup
    val (searchDeviceValue, setDeviceValue) = useState("") // For Gateway Device Data
    var gatewayDeviceInformation by useState(emptyMap<String, List<String>>())
    var gatewayDeviceInfoLoading by useState(false)
    var gatewayReloadDisabled by useState(false)
    var notificationsChecked by useState(false) // For the checkbox in the first card
    var percentageOfDevicesOfflineSetting by useState(10) // Default percentage for offline notifications

    var listPropertyStatistics by useState(emptyList<PropertyOfflineStatisticsModel>())
    var propertyLoadingStatistics by useState(emptyList<PropertyNotificationStatsModel>())
    var propertySmokeAlarmDurationStats by useState(emptyList<SmokeAlarmDurationRecordModel>())
    var propertyStatsLoading by useState(false)
    val (propertySearchValue, setPropertySearchValue) = useState("") // <-- State for Property Statistics search
    val (propertyNotificationSearchValue, setPropertyNotificationSearchValue) = useState("") // <-- State for Property Notification search
    val (smokeAlarmSearchValue, setSmokeAlarmSearchValue) = useState("") // <-- State for Smoke Alarm Duration search

    var appsheetDatabaseHealthStatus by useState(false)
    var appsheetDatabaseHealthLoading by useState(false)
    var appsheetDatabaseLastChecked by useState("")


    // Email Report Testing
    var propertyList: List<Property> by useState(emptyList())
    var selectedPropertyId: Int by useState(0)
    var adminEmail: List<UserDTO>? by useState(null)
    var (selectedEmail, setSelectedEmail) = useState("")
    var (selectReportOption, setReportOption) = useState(ReportFrequency.DAILY)
    var testEmailLoading: Boolean by useState(false)
    var testEmailResponse: String by useState("")
    var initialSettingsLoaded by useState(false)


    // --- Effects ---
    useEffectOnceWithCleanup {
        // Subscribe to ViewModel updates
        val viewStateJob = viewModel.viewState.onEach { viewState ->
            wirelessDeviceId = viewState.wirelessDeviceId
            deviceLocationLink = viewState.deviceLocationLink
            unitName = viewState.unitName
            propertyName = viewState.propertyName
            gatewayDeviceInformation = viewState.gatewayDeviceData
            appsheetDatabaseHealthStatus = viewState.appsheetDatabaseHealthStatus
            appsheetDatabaseHealthLoading = viewState.appsheetDatabaseHealthLoading
            appsheetDatabaseLastChecked = viewState.appsheetDatabaseLastChecked
            gatewayDeviceInfoLoading = viewState.gatewayDeviceDataLoading
            listPropertyStatistics = viewState.propertyOfflineStatistics
            propertyLoadingStatistics = viewState.propertyNotificationStats
            propertySmokeAlarmDurationStats = viewState.propertySmokeAlarmDurationStats
            propertyStatsLoading = viewState.propertyStatsLoading
            adminEmail = viewState.adminUsers
            testEmailLoading = viewState.testEmailSending
            testEmailResponse = viewState.testEmailResponse
            propertyList = viewState.propertyList
            // Reset reload button state when data updates might indicate completion
            gatewayReloadDisabled = viewState.gatewayDeviceDataLoading // Example: disable while loading
            percentageOfDevicesOfflineSetting = viewState.devicesOfflineSetting
            notificationsChecked = viewState.notificationsEnabled
        }.launchIn(viewModel.scope)
        val onLoadJob = viewModel.scope.launch {
            controller.onLoad()
        }
        onCleanup {
            viewStateJob.cancel()
            onLoadJob.cancel()
        }
    }

    // --- Table Columns State ---
    var propertyStatisticsColumns: Array<TableColumn> by useState(
        arrayOf(
            TableColumn(text = "PROPERTY", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Left),
            TableColumn(text = "TOTAL SENSORS", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "ONLINE", /* Updated Header */ sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "OFFLINE", /* Updated Header */ sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "NEVER INSTALLED", /* Updated Header */ sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "OVERALL NEED FIXING", /* Updated Header */ sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center)
        )
    )

    var propertyNotificationColumns: Array<TableColumn> by useState(
        arrayOf(
            TableColumn(text = "PROPERTY", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Left),
            TableColumn(text = "FA Email?", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "FA Text?", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "Has Tamper?", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "Tamper Email?", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "Tamper Text?", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "Has Leak?", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "Leak Email?", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
            TableColumn(text = "Leak Text?", sortOrder = TableColumn.SortOrder.Neutral, alignment = TableColumn.Alignment.Center),
        )
    )

    var smokeAlarmDurationColumns: Array<TableColumn> by useState(
        arrayOf(
            TableColumn(
                text = "PROPERTY",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            ),
            TableColumn(
                text = "UNIT",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Center
            ),
            TableColumn(
                text = "DEVICE",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Left
            ),
            TableColumn(
                text = "DURATION (MIN)",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Center
            ),
            TableColumn(
                text = "START TIME",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Center
            ),
            TableColumn(
                text = "END TIME",
                sortOrder = TableColumn.SortOrder.Neutral,
                alignment = TableColumn.Alignment.Center
            )
        )
    )

    // --- Event Handlers ---
    fun searchForId(value: String) {
        viewModel.scope.launch {
            controller.loadWirelessIdData(value)
        }
    }

    fun saveOfflineNotificationSettings(notificationsChecked: Boolean, percentage: Int) {
        viewModel.scope.launch {
            controller.saveOfflineNotificationSettings(notificationsChecked, percentage)
        }
    }

    fun getGatewayDeviceData(value: String) {
        viewModel.scope.launch {
            controller.loadGatewayDeviceData(value)
        }
    }

    fun reloadAllGatewayInfo() {
        // Disable button immediately for visual feedback
        gatewayReloadDisabled = true
        viewModel.scope.launch {
            controller.reloadAllGatewayInfo()
        }
    }

    fun sendTestEmail() {
        viewModel.scope.launch {
            controller.sendTestEmail(selectedEmail, selectedPropertyId, selectReportOption)
        }
    }

    fun checkAppsheetDatabaseHealth() {
        viewModel.scope.launch {
            controller.checkAppsheetDatabaseHealth()
        }
    }

    fun loadPropertyStatistics() {
        // Clear search when loading fresh data
        setPropertySearchValue("")
        viewModel.scope.launch {
            controller.loadPropertyStatistics()
        }
    }

    fun loadPropertyNotificationStatistics() {
        // Clear search when loading fresh data
        setPropertySearchValue("")
        viewModel.scope.launch {
            controller.loadPropertyNotificationStatistics()
        }
    }

    fun loadPropertySmokeAlarmDurationStatistics() {
        viewModel.scope.launch {
            controller.loadPropertySmokeAlarmDurationStatistics()
        }
    }

    section {
        // --- First Row of Cards ---
        div {
            className = SuperAdminToolsStyles.cardGrid
            // Device ID Lookup Card (Paper)...
            Paper {
                className = SuperAdminToolsStyles.dashboardCard
                elevation = 0
                div {
                    className = SuperAdminToolsStyles.cardHeader
                    Typography {
                        className = SuperAdminToolsStyles.cardTitle
                        +"Wireless Device ID Lookup"
                    }
                }
                div {
                    className = SuperAdminToolsStyles.cardContent
                    // Info section
                    div {
                        css {
                            backgroundColor = Color("#f5f5f5")
                            padding = 12.px
                            marginBottom = 16.px
                            borderRadius = 4.px
                            fontSize = 14.px
                            color = Color("#666")
                        }
                        Typography {
                            variant = TypographyVariant.body2
                            +"Enter a device ID (e.g., xxx-xxx, ls-XXXXXXXX, ws-XXXXXXXX) to find its AWS IoT wireless ID and location information."
                        }
                    }
                    div {
                        className = SuperAdminToolsStyles.inputGroup
                        TextField {
                            value = searchValue // Don't force lowercase here, do it in searchForId if needed
                            onChange = { event -> setSearchValue((event.target as HTMLInputElement).value) }
                        }
                        Button {
                            onClick = { searchForId(searchValue.lowercase()) }; +"Search"
                        } // Force lowercase on search
                    }
                    if (wirelessDeviceId.isNotEmpty() || deviceLocationLink.isNotEmpty() || propertyName.isNotEmpty()) {
                        +"$wirelessDeviceId - $propertyName"
                        Link {
                            to = deviceLocationLink
                            +deviceLocationLink
                        }
                        +"Unit Name: $unitName"

                    }
                }
            }

            Paper {
                className = SuperAdminToolsStyles.dashboardCard
                elevation = 0
                div { className = SuperAdminToolsStyles.cardHeader; Typography { +"Appsheet Database Health" } }
                div {
                    className = SuperAdminToolsStyles.cardContent
                    // Info section
                    div {
                        css {
                            backgroundColor = Color("#f5f5f5")
                            padding = 12.px
                            marginBottom = 16.px
                            borderRadius = 4.px
                            fontSize = 14.px
                            color = Color("#666")
                        }
                        Typography {
                            variant = TypographyVariant.body2
                            +"Check the connection status between our system and the AppSheet database. This verifies that the integration is working properly."
                        }
                    }
                    Button {
                        onClick = { checkAppsheetDatabaseHealth() }
                        disabled = appsheetDatabaseHealthLoading
                        +"Check Health"
                    }
                    if (appsheetDatabaseLastChecked.isNotEmpty()) {
                        div {
                            if (appsheetDatabaseHealthStatus) {
                                +"Appsheet Database is Healthy"
                            } else {
                                +"Appsheet Database is Unhealthy"
                            }
                        }
                        +"Last Checked: $appsheetDatabaseLastChecked"
                    }
                }
            }

            Paper {
                className = SuperAdminToolsStyles.dashboardCard
                elevation = 0
                div {
                    className = SuperAdminToolsStyles.cardHeader
                    Typography {
                        className = SuperAdminToolsStyles.cardTitle
                        +"Offline Devices Alerts"
                    }
                }
                div {
                    className = SuperAdminToolsStyles.cardContent
                    // Info section
                    div {
                        css {
                            backgroundColor = Color("#f5f5f5")
                            padding = 12.px
                            marginBottom = 16.px
                            borderRadius = 4.px
                            fontSize = 14.px
                            color = Color("#666")
                        }
                        Typography {
                            variant = TypographyVariant.body2
                            +"Set a percentage threshold of when to be notified for devices offline. You can also choose to not be notified at all."
                        }
                    }
                    div {
                        div {
                            className = SuperAdminToolsStyles.inputGroup
                            Slider {
                                marks = true
                                min = 0
                                max = 100
                                step = 5
                                value = percentageOfDevicesOfflineSetting
                                valueLabelDisplay = ValueLabelDisplay.AUTO.value
                                onChange = { event, newValue, number ->
                                    val updatedValue = newValue as Int
                                    percentageOfDevicesOfflineSetting = updatedValue
                                }
                            }
                        }
                        div {
                            FormControlLabel {
                                // 3. Pass the Checkbox as the `control` property
                                control = Checkbox.create {
                                    // All Checkbox-specific properties go here
                                    checked = notificationsChecked
                                    onChange = { event, newCheckedState ->
                                        // This lambda is called when the checkbox is clicked
                                        notificationsChecked = newCheckedState
                                    }
                                }
                                // 4. Set the label for the FormControlLabel
                                label = ReactNode("Notify me when devices are offline")
                            }
                            Button {
                                onClick = {
                                    saveOfflineNotificationSettings(
                                        notificationsChecked,
                                        percentageOfDevicesOfflineSetting
                                    )
                                }; +"Save Settings"
                            }
                        }
                    }

                    if (wirelessDeviceId.isNotEmpty() || deviceLocationLink.isNotEmpty() || propertyName.isNotEmpty()) {
                        +"$wirelessDeviceId - $propertyName"
                        Link {
                            to = deviceLocationLink
                            +deviceLocationLink
                        }
                        +"Unit Name: $unitName"

                    }
                }
            }
        }

        // --- Second row - Gateway Device Data ---
        div {
            className = SuperAdminToolsStyles.cardGrid
            // Gateway Device Data Card (Paper)...
            Paper {
                className = SuperAdminToolsStyles.dashboardCard
                elevation = 0
                div { className = SuperAdminToolsStyles.cardHeader
                    Typography { +"Gateway Device Data" }
                    Button {
                        onClick = { reloadAllGatewayInfo() }
                        disabled = gatewayReloadDisabled || gatewayDeviceInfoLoading // Disable if reloading or loading specific
                        +"Reload All Gateway Info"
                    }
                }
                div { className = SuperAdminToolsStyles.cardContent
                    // Info section
                    div {
                        css {
                            backgroundColor = Color("#f5f5f5")
                            padding = 12.px
                            marginBottom = 16.px
                            borderRadius = 4.px
                            fontSize = 14.px
                            color = Color("#666")
                        }
                        Typography {
                            variant = TypographyVariant.body2
                            +"Enter a Property ID to view gateway associations for all devices at that property. Use 'Reload All Gateway Info' to refresh gateway data across the entire system."
                        }
                    }
                    div { className = SuperAdminToolsStyles.inputGroup
                        TextField {
                            value = searchDeviceValue
                            onChange = { event -> setDeviceValue((event.target as HTMLInputElement).value) }
                        }
                        Button {
                            onClick = { getGatewayDeviceData(searchDeviceValue) }
                            disabled = gatewayDeviceInfoLoading
                            +"Get Data"

                        }
                    }
                    if (gatewayDeviceInfoLoading && gatewayDeviceInformation.isEmpty()) {
                        div { className = SuperAdminToolsStyles.loadingContainer; CircularProgress {} }
                    } else if (gatewayDeviceInformation.isNotEmpty()) {
                        div {
                            gatewayDeviceInformation.forEach { (key, value) ->
                                div {
                                    +"$key: ${value.joinToString(", ")}"
                                }
                            }
                        }

                    } else {
                        div { className = SuperAdminToolsStyles.emptyStateContainer
                            Typography { +"No gateway data available. Enter a Property ID and click 'Get Data'." }
                        }
                    }
                }
            }
            Paper {
                className = SuperAdminToolsStyles.dashboardCard
                elevation = 0
                div { className = SuperAdminToolsStyles.cardHeader
                    Typography { +"Test Email" }
                    Button {
                        onClick = { sendTestEmail() }
                        disabled = testEmailLoading || selectedEmail.isEmpty() || selectedPropertyId == 0
                        +"Send Email"
                    }
                }
                div { className = SuperAdminToolsStyles.cardContent
                    // Info section
                    div {
                        css {
                            backgroundColor = Color("#f5f5f5")
                            padding = 12.px
                            marginBottom = 16.px
                            borderRadius = 4.px
                            fontSize = 14.px
                            color = Color("#666")
                        }
                        Typography {
                            variant = TypographyVariant.body2
                            +"Test the email report system by sending a sample report to a super admin. Select recipient, report type, and property. Rate limited to 5 tests per 2 minutes."
                        }
                    }
                    div { className = SuperAdminToolsStyles.inputGroup
                        Select {
                            label = ReactNode("Email Selector")
                            labelId = "class-label"
                            value = selectedEmail
                            fullWidth = true
                            onChange = { event, _ ->
                                setSelectedEmail(event.target.value)
                            }
                            adminEmail?.forEach { user ->
                                MenuItem {
                                    value = user.email
                                    +"${user.name} (${user.email})"
                                }
                            }
                        }

                        Select {
                            label = ReactNode("Report Selector")
                            labelId = "class-label"
                            value = selectReportOption.displayName
                            fullWidth = true
                            onChange = { event, _ ->
                                val selectedOption = ReportFrequency.entries.find { it.displayName == event.target.value }
                                if (selectedOption != null) {
                                    setReportOption(selectedOption)
                                }
                            }
                            ReportFrequency.entries.forEach { report ->
                                MenuItem {
                                    value = report.displayName
                                    +report.displayName
                                }
                            }
                        }
                        Button {
                            onClick = { getGatewayDeviceData(searchDeviceValue) }
                            disabled = gatewayDeviceInfoLoading

                        }
                    }

                    div { className = SuperAdminToolsStyles.inputGroup
                        Autocomplete<AutocompleteProps<String>> {
                            onChange = { _, value, _, _ ->
                                val selectedProperty = propertyList.find { it.name == value }
                                if (selectedProperty != null) {
                                    selectedPropertyId = selectedProperty.id
                                    propertyName = selectedProperty.name
                                }
                            }
                            fullWidth = true
                            options = propertyList.map { it.name }.toTypedArray()
                            defaultValue = propertyList.firstOrNull()?.name
                            key = "property-list-autocomplete"
                            renderInput = { params ->
                                TextField.create {
                                    variant = FormControlVariant.outlined
                                    label = ReactNode("Property List")
                                    +params
                                }
                            }
                        }
                    }


                    div { className = SuperAdminToolsStyles.emptyStateContainer
                        Typography { +"Email Test Debugger is rate limited! Only 5 tests per 2 minutes!" }
                    }
                }
            }
        }

        // --- Full-width card - Property Wireless Statistics ---
        div {
            className = SuperAdminToolsStyles.tableGrid

            Paper {
                className = cx(SuperAdminToolsStyles.dashboardCard, SuperAdminToolsStyles.fullWidthCard)
                elevation = 0

                div { // Card Header
                    className = SuperAdminToolsStyles.cardHeader
                    Typography {
                        className = SuperAdminToolsStyles.cardTitle
                        +"Property Wireless Statistics"
                    }
                    // Header actions could go here if needed
                }

                div {
                    className = SuperAdminToolsStyles.tableCardContent

                    // Info section
                    div {
                        css {
                            backgroundColor = Color("#f5f5f5")
                            padding = 12.px
                            marginBottom = 16.px
                            borderRadius = 4.px
                            fontSize = 14.px
                            color = Color("#666")
                        }
                        Typography {
                            variant = TypographyVariant.body2
                            +"View wireless device statistics across all properties. Shows online/offline status, never installed devices, and devices needing attention. Click column headers to sort. Export data to CSV for reporting."
                        }
                    }

                    // --- Controls: Scan Button, Search, Export ---
                    div {

                        css {
                            display = Display.flex
                            flexDirection = FlexDirection.row
                            alignItems = AlignItems.center
                            gap = 16.px // Spacing between controls
                            marginBottom = 20.px // Space below controls
                            flexWrap = FlexWrap.wrap // Allow controls to wrap on smaller screens
                        }

                        // Scan Button
                        Button {
                            variant = ButtonVariant.contained
                            color = ButtonColor.primary
                            disabled = propertyStatsLoading
                            onClick = { loadPropertyStatistics() } // Clears search inside

                            div {
                                className = SuperAdminToolsStyles.buttonWithIcon
                                if (propertyStatsLoading && listPropertyStatistics.isEmpty()) { // Show spinner only on initial load
                                    CircularProgress { size = 20.px; color = CircularProgressColor.inherit; className = SuperAdminToolsStyles.infoIcon }
                                    +"Scanning..."
                                } else {
                                    +"Start Property Wireless Scan"
                                }
                            }
                        }

                        // Search Bar for Properties
                        TextField {
                            variant = FormControlVariant.outlined
                            size = Size.small
                            placeholder = "Search Properties..."
                            value = propertySearchValue
                            onChange = { event ->
                                setPropertySearchValue((event.target as HTMLInputElement).value)
                            }
                            css {
                                minWidth = 250.px
                                flexGrow = number(1.0)
                                maxWidth = 400.px
                            }
                        }

                        // Export CSV Button
                        Button {
                            variant = ButtonVariant.outlined
                            color = ButtonColor.primary
                            // Disable if no data OR if currently loading the initial list
                            disabled = listPropertyStatistics.isEmpty() || (propertyStatsLoading && listPropertyStatistics.isEmpty())
                            onClick = {
                                // 1. Filter data based on current search value
                                val filteredData = listPropertyStatistics.filter {
                                    it.propertyName.contains(propertySearchValue, ignoreCase = true)
                                }
                                // 2. Sort the filtered data based on current column sort state
                                val sortedData = controller.sortPropertyStatistics(propertyStatisticsColumns, filteredData)

                                // 3. Generate CSV content from the *sorted and filtered* data
                                val csvContent = controller.generatePropertyStatisticsCsv(sortedData)

                                // 4. Create a Blob
                                val blob = Blob(arrayOf(csvContent), BlobPropertyBag(type = "text/csv;charset=utf-8;"))

                                // 5. Trigger download
                                saveAs(blob, "property_wireless_statistics.csv")
                            }
                            +"Export CSV"
                        }
                    }

                    if (propertyStatsLoading && listPropertyStatistics.isEmpty()) {
                        div {
                            className = SuperAdminToolsStyles.loadingContainer
                            CircularProgress { color = CircularProgressColor.primary; size = 60.px }
                            Typography { variant = TypographyVariant.body1; +"Scanning properties, please wait..." }
                        }
                    }
                    val filteredStatistics = listPropertyStatistics.filter {
                        it.propertyName.contains(propertySearchValue, ignoreCase = true)
                    }
                    val sortedAndFilteredStatistics = controller.sortPropertyStatistics(
                        propertyStatisticsColumns,
                        filteredStatistics
                    )

                    // Render table container only if not initial loading OR if there's data (even if loading update)
                    if (!(propertyStatsLoading && listPropertyStatistics.isEmpty())) {
                        div { // Table Wrapper with Scroll
                            className = cx(TableStyles.scrolledTableContainer, SuperAdminToolsStyles.tableWrapper)
                            div { // Actual Table Structure
                                className = TableStyles.table

                                // Sort Bar
                                SortBar {
                                    columns = mapToControlColumns(propertyStatisticsColumns)
                                    includeLoading = true
                                    isLoading = propertyStatsLoading
                                    columnsChanged = { newColumns ->
                                        propertyStatisticsColumns = mapToStateColumns(newColumns)
                                    }
                                }

                                // --- Table Rows ---
                                if (sortedAndFilteredStatistics.isNotEmpty()) {
                                    sortedAndFilteredStatistics.forEach { stat ->
                                        div {
                                            className = cx(
                                                TableStyles.tableRow,
                                                if (stat.propertyId in listOf(182, 141, 130, 209, 118, 44, 147, 257)) ClassName { backgroundColor = Color("lightgray") } else null
                                            )

                                            Link {
                                                to = "${RoutePaths.properties}/${stat.propertyId}"
                                                div { className = TableStyles.tableCell; +stat.propertyName }
                                            } // Property Name
                                            div { className = TableStyles.tableCell; +"${stat.totalDevices}" } // Total
                                            div { className = TableStyles.tableCell; +"${stat.onlineNumber} (${stat.onlinePercentage.toInt()}%)" } // Online
                                            div { className = TableStyles.tableCell; +"${stat.offlineNumber} (${stat.offlinePercentage.toInt()}%)" } // Offline
                                            div { className = TableStyles.tableCell; +"${stat.neverInstalledNumber} (${stat.neverInstalledPercentage.toInt()}%)" } // Never Installed

                                            // Overall Need Fixing
                                            div {
                                                className = TableStyles.tableCell
                                                val combinedCount = stat.offlineNumber + stat.neverInstalledNumber
                                                val combinedPercentage = if (stat.totalDevices > 0) {
                                                    (combinedCount.toDouble() / stat.totalDevices) * 100
                                                } else { 0.0 }
                                                +"$combinedCount (${combinedPercentage.toInt()}%)"
                                            }
                                        }
                                    }
                                } else {
                                    // --- No Data / No Results Message ---
                                    div {
                                        className = TableStyles.tableRow
                                        div {
                                            className = TableStyles.tableCell
                                            css { textAlign = TextAlign.center; padding = 32.px; color = Color("#666") }

                                            +if (propertySearchValue.isNotBlank()) {
                                                "No properties match your search criteria \"$propertySearchValue\"."
                                            } else if (listPropertyStatistics.isEmpty() && !propertyStatsLoading) {
                                                "No data available. Click 'Start Property Scan' to retrieve statistics."
                                            } else {
                                                // This case might occur briefly during loading or if filtering clears everything
                                                "No statistics to display."
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // --- Full-width card - Property Notification  ---
        div {
            className = SuperAdminToolsStyles.tableGrid

            Paper {
                className = cx(SuperAdminToolsStyles.dashboardCard, SuperAdminToolsStyles.fullWidthCard)
                elevation = 0

                div { // Card Header
                    className = SuperAdminToolsStyles.cardHeader
                    Typography {
                        className = SuperAdminToolsStyles.cardTitle
                        +"Property Notification Statistics"
                    }
                    // Header actions could go here if needed
                }

                div {
                    className = SuperAdminToolsStyles.tableCardContent

                    // Info section
                    div {
                        css {
                            backgroundColor = Color("#f5f5f5")
                            padding = 12.px
                            marginBottom = 16.px
                            borderRadius = 4.px
                            fontSize = 14.px
                            color = Color("#666")
                        }
                        Typography {
                            variant = TypographyVariant.body2
                            +"View notification settings for each property. Shows which properties have email/text notifications enabled for FireAvert, tamper sensors, and leak sensors. Use this to ensure properties have proper alerting configured."
                        }
                    }

                    // --- Controls: Scan Button, Search, Export ---
                    div {

                        css {
                            display = Display.flex
                            flexDirection = FlexDirection.row
                            alignItems = AlignItems.center
                            gap = 16.px // Spacing between controls
                            marginBottom = 20.px // Space below controls
                            flexWrap = FlexWrap.wrap // Allow controls to wrap on smaller screens
                        }

                        // Scan Button
                        Button {
                            variant = ButtonVariant.contained
                            color = ButtonColor.primary
                            disabled = propertyStatsLoading
                            onClick = { loadPropertyNotificationStatistics() } // Clears search inside

                            div {
                                className = SuperAdminToolsStyles.buttonWithIcon
                                if (propertyStatsLoading && listPropertyStatistics.isEmpty()) { // Show spinner only on initial load
                                    CircularProgress { size = 20.px; color = CircularProgressColor.inherit; className = SuperAdminToolsStyles.infoIcon }
                                    +"Scanning..."
                                } else {
                                    +"Start Property Notification Scan"
                                }
                            }
                        }

                        // Search Bar for Properties
                        TextField {
                            variant = FormControlVariant.outlined
                            size = Size.small
                            placeholder = "Search Properties..."
                            value = propertyNotificationSearchValue
                            onChange = { event ->
                                setPropertyNotificationSearchValue((event.target as HTMLInputElement).value)
                            }
                            css {
                                minWidth = 250.px
                                flexGrow = number(1.0)
                                maxWidth = 400.px
                            }
                        }
                    }

                    if (propertyStatsLoading && propertyLoadingStatistics.isEmpty()) {
                        div {
                            className = SuperAdminToolsStyles.loadingContainer
                            CircularProgress { color = CircularProgressColor.primary; size = 60.px }
                            Typography { variant = TypographyVariant.body1; +"Scanning properties, please wait..." }
                        }
                    }
                    val filteredStatistics = propertyLoadingStatistics.filter {
                        it.propertyName.contains(propertyNotificationSearchValue, ignoreCase = true)
                    }
                    val sortedAndFilteredStatistics = controller.sortPropertyNotificationStatistics(
                        propertyNotificationColumns,
                        filteredStatistics
                    )

                    // Render table container only if not initial loading OR if there's data (even if loading update)
                    if (!(propertyStatsLoading && propertyLoadingStatistics.isEmpty())) {
                        div { // Table Wrapper with Scroll
                            className = cx(TableStyles.scrolledTableContainer, SuperAdminToolsStyles.tableWrapper)
                            div { // Actual Table Structure
                                className = TableStyles.table

                                // Sort Bar
                                SortBar {
                                    columns = mapToControlColumns(propertyNotificationColumns)
                                    includeLoading = true
                                    isLoading = propertyStatsLoading
                                    columnsChanged = { newColumns ->
                                        propertyNotificationColumns = mapToStateColumns(newColumns)
                                    }
                                }

                                // --- Table Rows ---
                                if (sortedAndFilteredStatistics.isNotEmpty()) {
                                    sortedAndFilteredStatistics.forEach { stat ->
                                        div {
                                            className = cx(
                                                TableStyles.tableRow,
                                                if (stat.propertyId in listOf(182, 141, 130, 209, 118, 44, 147, 257)) ClassName { backgroundColor = Color("lightgray") } else null
                                            )

                                            Link {
                                                to = "${RoutePaths.properties}/${stat.propertyId}"
                                                div { className = TableStyles.tableCell; +stat.propertyName }
                                            } // Property Name
                                            div { className = TableStyles.tableCell; +"${stat.hasEmailNotificationForLeak}" }
                                            div { className = TableStyles.tableCell; +"${stat.hasTextNotificationsForFA}" }
                                            div { className = TableStyles.tableCell; +"${stat.hasTamperSensors}" }
                                            div { className = TableStyles.tableCell; +"${stat.hasEmailNotificationForTamper}" }
                                            div { className = TableStyles.tableCell; +"${stat.hasTextNotificationForTamper}" }
                                            div { className = TableStyles.tableCell; +"${stat.hasLeakSensors}" }
                                            div { className = TableStyles.tableCell; +"${stat.hasEmailNotificationForLeak}" }
                                            div { className = TableStyles.tableCell; +"${stat.hasTextNotificationsForLeak}" }

                                        }
                                    }
                                } else {
                                    // --- No Data / No Results Message ---
                                    div {
                                        className = TableStyles.tableRow
                                        div {
                                            className = TableStyles.tableCell
                                            css { textAlign = TextAlign.center; padding = 32.px; color = Color("#666") }

                                            +if (propertySearchValue.isNotBlank()) {
                                                "No properties match your search criteria \"$propertySearchValue\"."
                                            } else if (listPropertyStatistics.isEmpty() && !propertyStatsLoading) {
                                                "No data available. Click 'Start Property Scan' to retrieve statistics."
                                            } else {
                                                // This case might occur briefly during loading or if filtering clears everything
                                                "No statistics to display."
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // --- Full-width card - Smoke Alarm Duration Statistics ---
        div {
            className = SuperAdminToolsStyles.tableGrid

            Paper {
                className = cx(SuperAdminToolsStyles.dashboardCard, SuperAdminToolsStyles.fullWidthCard)
                elevation = 0

                div { // Card Header
                    className = SuperAdminToolsStyles.cardHeader
                    Typography {
                        className = SuperAdminToolsStyles.cardTitle
                        +"Smoke Alarm Duration Statistics"
                    }
                    // Header actions could go here if needed
                }

                div {
                    className = SuperAdminToolsStyles.tableCardContent

                    // Info section
                    div {
                        css {
                            backgroundColor = Color("#f5f5f5")
                            padding = 12.px
                            marginBottom = 16.px
                            borderRadius = 4.px
                            fontSize = 14.px
                            color = Color("#666")
                        }
                        Typography {
                            variant = TypographyVariant.body2
                            +"View smoke alarm duration statistics showing how long smoke alarms sounded before being silenced. Includes property, unit, device info, duration in minutes, and start/end times. Useful for analyzing response times and patterns."
                        }
                    }

                    // --- Controls: Scan Button, Search, Export ---
                    div {

                        css {
                            display = Display.flex
                            flexDirection = FlexDirection.row
                            alignItems = AlignItems.center
                            gap = 16.px // Spacing between controls
                            marginBottom = 20.px // Space below controls
                            flexWrap = FlexWrap.wrap // Allow controls to wrap on smaller screens
                        }

                        // Scan Button
                        Button {
                            variant = ButtonVariant.contained
                            color = ButtonColor.primary
                            disabled = propertyStatsLoading
                            onClick = { loadPropertySmokeAlarmDurationStatistics() }

                            div {
                                className = SuperAdminToolsStyles.buttonWithIcon
                                if (propertyStatsLoading && propertySmokeAlarmDurationStats.isEmpty()) { // Show spinner only on initial load
                                    CircularProgress {
                                        size = 20.px; color = CircularProgressColor.inherit; className =
                                        SuperAdminToolsStyles.infoIcon
                                    }
                                    +"Loading..."
                                } else {
                                    +"Load Smoke Alarm Duration Stats"
                                }
                            }
                        }

                        // Search Bar for smoke alarm records
                        TextField {
                            variant = FormControlVariant.outlined
                            size = Size.small
                            placeholder = "Search smoke alarm records..."
                            value = smokeAlarmSearchValue
                            onChange = { event ->
                                setSmokeAlarmSearchValue((event.target as HTMLInputElement).value)
                            }
                            css {
                                minWidth = 250.px
                                flexGrow = number(1.0)
                                maxWidth = 400.px
                            }
                        }

                        // Export CSV Button
                        Button {
                            variant = ButtonVariant.outlined
                            color = ButtonColor.primary
                            // Disable if no data OR if currently loading the initial list
                            disabled =
                                propertySmokeAlarmDurationStats.isEmpty() || (propertyStatsLoading && propertySmokeAlarmDurationStats.isEmpty())
                            onClick = {
                                // 1. Filter data based on current search value
                                val filteredData = propertySmokeAlarmDurationStats.filter {
                                    it.propertyName.contains(smokeAlarmSearchValue, ignoreCase = true) ||
                                            it.unitNumber.contains(smokeAlarmSearchValue, ignoreCase = true) ||
                                            it.deviceName.contains(smokeAlarmSearchValue, ignoreCase = true)
                                }
                                // 2. Sort the filtered data based on current column sort state
                                val sortedData =
                                    controller.sortSmokeAlarmDurationRecords(smokeAlarmDurationColumns, filteredData)

                                // 3. Generate CSV content from the *sorted and filtered* data
                                val csvContent = controller.generateSmokeAlarmDurationRecordsCsv(sortedData)

                                // 4. Create a Blob
                                val blob = Blob(arrayOf(csvContent), BlobPropertyBag(type = "text/csv;charset=utf-8;"))

                                // 5. Trigger download
                                saveAs(blob, "smoke_alarm_duration_statistics.csv")
                            }
                            +"Export CSV"
                        }
                    }

                    if (propertyStatsLoading && propertySmokeAlarmDurationStats.isEmpty()) {
                        div {
                            className = SuperAdminToolsStyles.loadingContainer
                            CircularProgress { color = CircularProgressColor.primary; size = 60.px }
                            Typography {
                                variant = TypographyVariant.body1; +"Loading smoke alarm duration data, please wait..."
                            }
                        }
                    }
                    val filteredRecords = propertySmokeAlarmDurationStats.filter {
                        it.propertyName.contains(smokeAlarmSearchValue, ignoreCase = true) ||
                                it.unitNumber.contains(smokeAlarmSearchValue, ignoreCase = true) ||
                                it.deviceName.contains(smokeAlarmSearchValue, ignoreCase = true)
                    }
                    val sortedAndFilteredRecords = controller.sortSmokeAlarmDurationRecords(
                        smokeAlarmDurationColumns,
                        filteredRecords
                    )

                    // Render table container only if not initial loading OR if there's data (even if loading update)
                    if (!(propertyStatsLoading && propertySmokeAlarmDurationStats.isEmpty())) {
                        div { // Table Wrapper with Scroll
                            className = cx(TableStyles.scrolledTableContainer, SuperAdminToolsStyles.tableWrapper)
                            div { // Actual Table Structure
                                className = TableStyles.table

                                // Sort Bar
                                SortBar {
                                    columns = mapToControlColumns(smokeAlarmDurationColumns)
                                    includeLoading = true
                                    isLoading = propertyStatsLoading
                                    columnsChanged = { newColumns ->
                                        smokeAlarmDurationColumns = mapToStateColumns(newColumns)
                                    }
                                }

                                // --- Table Rows ---
                                if (sortedAndFilteredRecords.isNotEmpty()) {
                                    sortedAndFilteredRecords.forEach { record ->
                                        div {
                                            className = TableStyles.tableRow

                                            Link {
                                                to = "${RoutePaths.properties}/${record.propertyId}"
                                                div { className = TableStyles.tableCell; +record.propertyName }
                                            } // Property Name
                                            div { className = TableStyles.tableCell; +record.unitNumber } // Unit
                                            div { className = TableStyles.tableCell; +record.deviceName } // Device
                                            div {
                                                className =
                                                    TableStyles.tableCell; +record.durationMinutes.toOneDecimal()
                                            } // Duration
                                            div {
                                                className = TableStyles.tableCell; +record.startTime.substringBefore(
                                                'T'
                                            )
                                            } // Start Time (date only)
                                            div {
                                                className = TableStyles.tableCell; +record.endTime.substringBefore('T')
                                            } // End Time (date only)
                                        }
                                    }
                                } else {
                                    // --- No Data / No Results Message ---
                                    div {
                                        className = TableStyles.tableRow
                                        div {
                                            className = TableStyles.tableCell
                                            css { textAlign = TextAlign.center; padding = 32.px; color = Color("#666") }

                                            +if (smokeAlarmSearchValue.isNotBlank()) {
                                                "No smoke alarm records match your search criteria \"$smokeAlarmSearchValue\"."
                                            } else if (propertySmokeAlarmDurationStats.isEmpty() && !propertyStatsLoading) {
                                                "No data available. Click 'Load Smoke Alarm Duration Stats' to retrieve statistics."
                                            } else {
                                                // This case might occur briefly during loading or if filtering clears everything
                                                "No smoke alarm duration statistics to display."
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}